import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Input, Select, TextArea } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Preloader from "~/components/Preloader";
import Modal from "~/shared/ui/Fields/Modal";
import {
  ILicenseRequestRow,
  ILicenseRequestTransitionItem,
  useGetLicenseRequestById,
} from "~/entity/Licenses";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { parseNumeric } from "~/shared/lib/parseNumeric";

import s from "~/pages/LicenseManagement/LicenseManagement.less";

export type LicenseDecisionValue = "approve" | "reject";

export interface ILicenseDecisionForm {
  decision: { value: LicenseDecisionValue; label: string } | null;
  comment?: string;
  positions: Array<{
    discountPercent: string;
    discountAmount: string;
    priceWithDiscount: string;
  }>;
}

interface IDecisionPosition {
  itemId: string;
  positionName: string;
  quantity: number;
  unitPrice?: number;
  discountPercent?: number | null;
  discountAmount?: number | null;
  priceWithDiscount?: string | null;
}

interface LicenseDecisionModalProps {
  requests: ILicenseRequestRow[];
  onConfirm: (
    actionId: string,
    decision: LicenseDecisionValue,
    comment?: string,
    items?: ILicenseRequestTransitionItem[],
  ) => void;
}

type DiscountSource = "percent" | "amount" | "price";

const DISCOUNT_RECALC_DELAY = 400;

const formatNumber = (value: number) => String(Math.round(value * 100) / 100);

const clampPercentInput = (raw: string) => {
  if (!raw) {
    return raw;
  }
  const parsed = parseNumeric(raw);
  if (parsed === null) {
    return raw;
  }
  if (parsed > 100) {
    return "100";
  }
  if (parsed < 0) {
    return "0";
  }
  return raw;
};

const toOptionalNumber = (raw: string) => {
  const parsed = parseNumeric(raw);
  return parsed === null ? undefined : parsed;
};

const LicenseDecisionModal = ({
  requests,
  onConfirm,
}: LicenseDecisionModalProps) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useURLParamsGetter<{
    openModal: string;
    currentId: string;
  }>();
  const setParams = useURLParamsSetter();
  const isOpen = openModal === "Decision";

  const currentRequest = useMemo(
    () => requests.find((item) => item.id === currentId) || null,
    [requests, currentId],
  );

  const { request, isLoading } = useGetLicenseRequestById(
    isOpen && currentId ? currentId : undefined,
  );

  const positions = useMemo<IDecisionPosition[]>(
    () =>
      (request?.items || []).map((item) => ({
        itemId: item.id,
        positionName: item.positions?.length
          ? item.positions.map((p) => `${p.code}. ${p.name}`).join(", ")
          : item.positionName || "-",
        quantity: item.quantity,
        unitPrice: item.unitPrice == null ? undefined : Number(item.unitPrice),
        discountPercent: item.discountPercent,
        discountAmount: item.discountAmount,
        priceWithDiscount: item.priceWithDiscount,
      })),
    [request],
  );

  const decisionOptions = [
    {
      value: "approve" as const,
      label: t("licenseManagement.decisionModal.approve"),
    },
    {
      value: "reject" as const,
      label: t("licenseManagement.decisionModal.reject"),
    },
  ];

  const defaultValues: ILicenseDecisionForm = useMemo(
    () => ({
      decision: null,
      comment: "",
      positions: positions.map((item) => ({
        discountPercent:
          item.discountPercent === null || item.discountPercent === undefined
            ? ""
            : String(item.discountPercent),
        discountAmount:
          item.discountAmount === null || item.discountAmount === undefined
            ? ""
            : String(item.discountAmount),
        priceWithDiscount:
          item.priceWithDiscount === null ||
          item.priceWithDiscount === undefined
            ? ""
            : String(item.priceWithDiscount),
      })),
    }),
    [positions],
  );

  const { control, handleSubmit, reset, watch, setValue, getValues } =
    useForm<ILicenseDecisionForm>({
      defaultValues,
    });

  const decisionValue = watch("decision");
  const commentValue = watch("comment");
  const positionsValue = watch("positions");
  const isReject = decisionValue?.value === "reject";
  const isApprove = decisionValue?.value === "approve";
  const pricesFilled = (positionsValue || []).every(
    (position) => parseNumeric(position.priceWithDiscount || "") !== null,
  );
  const canConfirm =
    !isLoading &&
    Boolean(decisionValue?.value) &&
    (!isReject || Boolean(commentValue?.trim())) &&
    (!isApprove || pricesFilled);

  const totalCost = positions.reduce(
    (sum, position, index) =>
      sum +
      position.quantity *
        (parseNumeric(positionsValue?.[index]?.priceWithDiscount || "") || 0),
    0,
  );

  const closeModal = () =>
    setParams({ openModal: undefined, currentId: undefined });

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen, reset, defaultValues]);

  const recalcTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );

  useEffect(() => {
    const timers = recalcTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const applyDiscountFromSource = useCallback(
    (index: number, source: DiscountSource, rawValue: string) => {
      const percentName = `positions.${index}.discountPercent` as const;
      const amountName = `positions.${index}.discountAmount` as const;
      const priceName = `positions.${index}.priceWithDiscount` as const;
      const price = positions[index]?.unitPrice;
      const value = parseNumeric(rawValue);

      if (source === "price") {
        if (value === null || !price || price <= 0) {
          setValue(percentName, "");
          setValue(amountName, "");
          return;
        }

        const amount = Math.max(0, price - value);
        setValue(amountName, formatNumber(amount), { shouldDirty: true });
        setValue(
          percentName,
          formatNumber(Math.min(100, (amount / price) * 100)),
          {
            shouldDirty: true,
          },
        );
        return;
      }

      if (!price || price <= 0 || value === null) {
        setValue(source === "percent" ? amountName : percentName, "");
        setValue(priceName, "");
        return;
      }

      if (source === "percent") {
        const percent = Math.min(100, Math.max(0, value));
        const amount = (price * percent) / 100;
        setValue(amountName, formatNumber(amount), { shouldDirty: true });
        setValue(priceName, formatNumber(Math.max(0, price - amount)), {
          shouldDirty: true,
        });
        return;
      }

      setValue(
        percentName,
        formatNumber(Math.min(100, (value / price) * 100)),
        {
          shouldDirty: true,
        },
      );
      setValue(priceName, formatNumber(Math.max(0, price - value)), {
        shouldDirty: true,
      });
    },
    [positions, setValue],
  );

  const scheduleDiscountRecalc = useCallback(
    (
      index: number,
      source: DiscountSource,
      rawValue: string,
      immediate = false,
    ) => {
      const key = `${index}.${source}`;
      const existing = recalcTimers.current[key];
      if (existing) {
        clearTimeout(existing);
      }

      const run = () => {
        delete recalcTimers.current[key];
        applyDiscountFromSource(index, source, rawValue);
      };

      if (immediate) {
        run();
        return;
      }

      recalcTimers.current[key] = setTimeout(run, DISCOUNT_RECALC_DELAY);
    },
    [applyDiscountFromSource],
  );

  const handleDiscountFieldChange = (
    index: number,
    source: DiscountSource,
    rawValue: string,
    onChange: (value: string) => void,
    immediate = false,
  ) => {
    onChange(rawValue);
    scheduleDiscountRecalc(index, source, rawValue, immediate);
  };

  const flushPendingRecalcs = () => {
    Object.entries(recalcTimers.current).forEach(([key, timer]) => {
      clearTimeout(timer);
      const [index, source] = key.split(".");
      const fieldName =
        source === "percent"
          ? (`positions.${index}.discountPercent` as const)
          : source === "amount"
            ? (`positions.${index}.discountAmount` as const)
            : (`positions.${index}.priceWithDiscount` as const);
      applyDiscountFromSource(
        Number(index),
        source as DiscountSource,
        getValues(fieldName) || "",
      );
    });
    recalcTimers.current = {};
  };

  const submit = handleSubmit((data) => {
    if (!data.decision?.value || !currentId) {
      return;
    }

    if (data.decision.value === "reject" && !data.comment?.trim()) {
      return;
    }

    const items: ILicenseRequestTransitionItem[] = positions.map(
      (position, index) => ({
        id: position.itemId,
        discountPercent: toOptionalNumber(
          data.positions[index]?.discountPercent || "",
        ),
        discountAmount: toOptionalNumber(
          data.positions[index]?.discountAmount || "",
        ),
        priceWithDiscount: toOptionalNumber(
          data.positions[index]?.priceWithDiscount || "",
        ),
      }),
    );

    onConfirm(
      currentId,
      data.decision.value,
      data.comment,
      data.decision.value === "approve" ? items : undefined,
    );
    closeModal();
  });

  return (
    <Modal
      isOpen={isOpen}
      title={t("licenseManagement.decisionModal.title")}
      actionClose={closeModal}
      w={948}
    >
      <div className={s.modalForm}>
        <div className={s.infoBanner}>
          <span>
            <strong>
              {t("licenseManagement.decisionModal.organization")}:
            </strong>{" "}
            {currentRequest?.dealerName || currentRequest?.organization || "-"}
          </span>
        </div>

        {isLoading ? (
          <Preloader />
        ) : (
          <div className={s.modalRows}>
            {positions.map((position, index) => (
              <React.Fragment key={position.itemId}>
                <div className={s.modalRow}>
                  <div className={s.modalField}>
                    <span className={s.modalLabelBold}>
                      {t("licenseManagement.decisionModal.position")}
                    </span>
                    <Input value={position.positionName} disabled />
                  </div>
                  <div className={s.modalField}>
                    <span className={s.modalLabel}>
                      {t("licenseManagement.decisionModal.quantity")}
                    </span>
                    <Input value={String(position.quantity)} disabled />
                  </div>
                </div>
                <div className={s.modalRow}>
                  <div className={s.modalField}>
                    <span className={s.modalLabel}>
                      {t("licenseManagement.decisionModal.discountPercent")}
                    </span>
                    <Controller
                      name={`positions.${index}.discountPercent`}
                      control={control}
                      render={({ onChange, value, onBlur }) => (
                        <Input
                          value={value}
                          onChange={(event) =>
                            handleDiscountFieldChange(
                              index,
                              "percent",
                              clampPercentInput(event.target.value),
                              onChange,
                            )
                          }
                          onBlur={() => {
                            handleDiscountFieldChange(
                              index,
                              "percent",
                              clampPercentInput(
                                getValues(
                                  `positions.${index}.discountPercent`,
                                ) || "",
                              ),
                              onChange,
                              true,
                            );
                            onBlur();
                          }}
                          placeholder={t(
                            "licenseManagement.filter.inputPlaceholder",
                          )}
                        />
                      )}
                    />
                  </div>
                  <div className={s.modalField}>
                    <span className={s.modalLabel}>
                      {t("licenseManagement.decisionModal.discountAmount")}
                    </span>
                    <Controller
                      name={`positions.${index}.discountAmount`}
                      control={control}
                      render={({ onChange, value, onBlur }) => (
                        <Input
                          value={value}
                          onChange={(event) =>
                            handleDiscountFieldChange(
                              index,
                              "amount",
                              event.target.value,
                              onChange,
                            )
                          }
                          onBlur={() => {
                            handleDiscountFieldChange(
                              index,
                              "amount",
                              getValues(`positions.${index}.discountAmount`) ||
                                "",
                              onChange,
                              true,
                            );
                            onBlur();
                          }}
                          placeholder={t(
                            "licenseManagement.filter.inputPlaceholder",
                          )}
                        />
                      )}
                    />
                  </div>
                  <div className={s.modalField}>
                    <span className={s.modalLabel}>
                      {t("licenseManagement.decisionModal.priceWithDiscount")}
                    </span>
                    <Controller
                      name={`positions.${index}.priceWithDiscount`}
                      control={control}
                      render={({ onChange, value, onBlur }) => (
                        <Input
                          value={value}
                          onChange={(event) =>
                            handleDiscountFieldChange(
                              index,
                              "price",
                              event.target.value,
                              onChange,
                            )
                          }
                          onBlur={() => {
                            handleDiscountFieldChange(
                              index,
                              "price",
                              getValues(
                                `positions.${index}.priceWithDiscount`,
                              ) || "",
                              onChange,
                              true,
                            );
                            onBlur();
                          }}
                          placeholder={t(
                            "licenseManagement.filter.inputPlaceholder",
                          )}
                        />
                      )}
                    />
                  </div>
                </div>
              </React.Fragment>
            ))}

            <div className={s.modalRow}>
              <div className={s.modalField}>
                <span className={s.modalLabelBold}>
                  {t("licenseManagement.decisionModal.totalCost")}
                </span>
                <Input value={formatNumber(totalCost)} disabled />
              </div>
            </div>
          </div>
        )}

        <div className={s.divider} />

        <div className={s.modalRows}>
          <div className={s.modalField}>
            <span className={s.modalLabelBold}>
              {t("licenseManagement.decisionModal.decision")}
            </span>
            <Controller
              name="decision"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  labelInValue
                  value={value}
                  onChange={onChange}
                  options={decisionOptions}
                  placeholder={t("licenseManagement.filter.notSelected")}
                />
              )}
            />
          </div>

          <div className={s.modalField}>
            <span className={s.modalLabel}>
              {t("licenseManagement.decisionModal.comment")}
            </span>
            <Controller
              name="comment"
              control={control}
              render={({ onChange, value, onBlur }) => (
                <TextArea
                  value={value}
                  onChange={(event) => onChange(event.target.value)}
                  onBlur={onBlur}
                  placeholder={t("licenseManagement.filter.inputPlaceholder")}
                  rows={3}
                />
              )}
            />
          </div>
        </div>

        <div className={s.modalButtons}>
          <Button
            bold
            color="secondary"
            fontSize="14"
            size="medium"
            radius={5}
            onClick={closeModal}
          >
            {t("licenseManagement.decisionModal.close")}
          </Button>
          <Button
            bold
            color="primary"
            fontSize="14"
            size="medium"
            radius={5}
            disabled={!canConfirm}
            onClick={() => {
              flushPendingRecalcs();
              submit();
            }}
          >
            {t("licenseManagement.decisionModal.confirm")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LicenseDecisionModal;
